import { useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import { Box, Icons, Image, Text, TextButton } from '@opendoor/bricks-next';

import { useT } from 'helpers/i18n';

interface Review {
  rating: number;
  reviewer: {
    city: string;
    productType: string;
    name?: string;
  };
  excerpt?: string;
  excerptKey?: string;
}

const STUBBED_REVIEWS: Review[] = [
  {
    rating: 5,
    excerptKey: 'socialProof.review.sanAntonio',
    reviewer: {
      city: 'San Antonio',
      productType: 'sell',
    },
  },
  {
    rating: 5,
    excerptKey: 'socialProof.review.okc',
    reviewer: {
      city: 'Oklahoma City',
      productType: 'sell',
    },
  },
  {
    rating: 5,
    excerptKey: 'socialProof.review.phoenix',
    reviewer: {
      city: 'Phoenix',
      productType: 'sell',
    },
  },
];

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches);
  }, []);

  return prefersReducedMotion;
};

export const SocialProofBox = ({ autoplayInterval = 4000 }: { autoplayInterval?: number }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const intervalRef = useRef<NodeJS.Timeout>();
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentReviewIndex((prevIndex) =>
        prevIndex === STUBBED_REVIEWS.length - 1 ? 0 : prevIndex + 1,
      );
    }, autoplayInterval);

    return () => clearInterval(intervalRef.current);
  }, [prefersReducedMotion, autoplayInterval]);

  return (
    <CarouselFormat
      reviews={STUBBED_REVIEWS}
      currentIndex={currentReviewIndex}
      interval={autoplayInterval}
    />
  );
};

const FiveStarBanner = ({ rating }: { rating: number }) => (
  <Box flexDirection="row" gap="$1x">
    {new Array(rating).fill('star').map((_, index) => (
      <Image
        key={index}
        alt="Rating star"
        source={{
          height: 16,
          uri: 'https://imgdrop.imgix.net/2024-11-1732263853253-76957.svg',
          width: 16,
        }}
      />
    ))}
  </Box>
);

// takes the interval with `infinite` instead of redrawing
// the content constantly to trigger animation, may diverge over time
const fadeInCss = (interval: number, index: number) => css`
  transition: all 1s ease-in-out;
  animation: fadeIn-${index} ${interval}ms infinite ease-in-out;

  @keyframes fadeIn-${index} {
    0% {
      opacity: 0;
    }
    25% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

const ReviewNav = () => {
  const { t } = useT();
  const goToReviews = useCallback(() => window.location.assign('https://opendoor.com/reviews'), []);

  return (
    <TextButton
      analyticsName="social-proof-carousel-see-reviews"
      aria-label={t('socialProof.review.seeReviews', 'See reviews')}
      onPress={goToReviews}
      variant="primary"
      label={t('socialProof.review.seeReviews', 'See reviews')}
      fontWeight="regular"
      size="small"
      width={88}
      alignItems="flex-end"
      $smallerThanLG={{
        size: 'xsmall',
        width: 76,
      }}
    />
  );
};

const CarouselFormat = ({
  reviews,
  currentIndex,
  interval,
}: {
  reviews: Review[];
  currentIndex: number;
  interval: number;
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { t } = useT();

  if (reviews.length === 0) return null;

  const currentReview = reviews[currentIndex];

  return (
    <Box
      flexDirection="row"
      justifyContent="space-between"
      gap="$5x"
      borderRadius="$12x"
      width={652}
      padding="$12x"
      style={{
        background: 'rgba(242, 241, 240, 0.64)',
        backdropFilter: 'blur(20px)',
        flex: 'auto',
      }}
      $smallerThanLG={{
        width: '100%',
        padding: '$8x',
        flexDirection: 'column',
        gap: '$2x',
      }}
      $smallerThanMD={{
        minHeight: 128,
        borderRadius: '$borderAllowance',
      }}
    >
      <Box
        flexDirection="column"
        width="100%"
        style={{ flex: 'auto' }}
        css={prefersReducedMotion ? null : fadeInCss(interval, currentIndex)}
      >
        <FiveStarBanner rating={currentReview.rating} />
        <Box flexDirection="row" alignItems="center" gap="$2x">
          <Text
            tag="span"
            typography="$bodySmall"
            color="$contentSecondary"
            $smallerThanLG={{
              typography: '$bodyXsmall',
            }}
          >
            {t('socialProof.review.sellerIn', 'Seller in')}&nbsp;{currentReview.reviewer.city}
          </Text>
          <Text
            tag="p"
            typography="$labelSmall"
            color="$contentAccentBrand"
            $smallerThanLG={{
              typography: '$labelXsmall',
            }}
          >
            {currentReview.reviewer?.name ||
              t('socialProof.review.verifiedCustomer', 'Verified Customer')}
          </Text>
          <Icons.Checkmark size={20} color="$contentAccentBrand" />
        </Box>
        <Box display="flex" justifyContent="space-between" flexDirection="row" gap="$4x">
          <Text
            tag="span"
            typography="$bodyMedium"
            color="$contentPrimary"
            $smallerThanLG={{
              typography: '$bodySmall',
            }}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {currentReview?.excerpt ||
              (currentReview.excerptKey &&
                t(
                  currentReview.excerptKey as Parameters<typeof t>[0],
                  'A great experience from the beginning...',
                ))}
          </Text>
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <ReviewNav />
      </Box>
    </Box>
  );
};
